import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  Box, 
  Toolbar, 
  useTheme,
  Button,
  IconButton,
  Badge,
} from '@mui/material';
import { ArrowLeft as ArrowLeftIcon, Menu as MenuIcon } from 'lucide-react';
import { useEventEdition } from '../contexts/EventEditionContext';
import useLogCounts from '../hooks/useLogCounts';
import RegistrationsPanel from '../components/admin/RegistrationsPanel';
import TemplatesPanel from '../components/admin/TemplatesPanel';
import EventEditionsPanel from '../components/admin/EventEditionsPanel';
import CodeListPanel from '../components/admin/CodeListPanel';
import ActionRequestsPanel from '../components/admin/ActionRequestsPanel';
import AdminTasksPanel from '../components/admin/AdminTasksPanel';
import BulkEmailPanel from '../components/admin/BulkEmailPanel';
import EventEditionSelector from '../components/EventEditionSelector';
import AdminLandingPanel from '../components/admin/AdminLandingPanel';
import ResultsStatusBanner from '../components/admin/ResultsStatusBanner';
import AnalyticsPanel from '../components/admin/AnalyticsPanel';
import BibAssignmentPanel from '../components/admin/BibAssignmentPanel';
import MOResultsImportPanel from '../components/admin/MOResultsImportPanel';
import MOVolunteerPanel from '../components/admin/MOVolunteerPanel';
import LogsPanel from '../components/admin/LogsPanel';
import MmcEmailPocPanel from '../components/admin/MmcEmailPocPanel';
import KUTCResultExportPanel from '../components/admin/KUTCResultExportPanel';
import DepositRefundPanel from '../components/admin/DepositRefundPanel';

import { adminSections, AdminSectionKey } from '../constants/adminSections';
type SectionKey = AdminSectionKey | 'landing';


const AdminPage: React.FC = () => {
  useEventEdition();
  const [active, setActive] = useState<SectionKey>('landing');
  const theme = useTheme();
  const { unresolved, needsAttention } = useLogCounts();
  // Define drawer width and make it initially closed for all devices
  const drawerWidth = 180; // Width for drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sections that need EventEditionSelector
  const sectionsNeedingEventEdition = [
    'registrations',
    'actions',
    'editions',
    'bulkemail',
    'bibassignment',
    'resultsimport',
    'kutcexport',
    'depositrefunds',
    'movolunteers',
  ];

  const needsEventEdition = active !== 'landing' && sectionsNeedingEventEdition.includes(active as string);

  // Listen for admin section changes from AppHeader
  useEffect(() => {
    const sectionHandler = (e: CustomEvent<string>) => {
      setActive(e.detail as SectionKey);
    };
    window.addEventListener('setAdminSection', sectionHandler as EventListener);
    return () => window.removeEventListener('setAdminSection', sectionHandler as EventListener);
  }, []);

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
            position: 'fixed',
            // Use theme's toolbar height to position below the AppBar
            top: (theme) => `${theme.mixins.toolbar.minHeight}px`,
            height: (theme) => `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
            zIndex: (theme) => theme.zIndex.appBar - 1, // Make sure drawer is below AppBar
          },
        }}
      >
        <Toolbar />
        <List>
          {adminSections.map((section) => (
            <ListItem
              button
              key={section.key}
              selected={active === section.key}
              onClick={() => setActive(section.key)}
            >
              <ListItemText 
                primary={section.label}
                secondary={
                  section.key === 'logs' ? (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      {unresolved > 0 && (
                        <Badge 
                          badgeContent={unresolved} 
                          color="primary"
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: '16px', height: '16px' } }}
                        />
                      )}
                      {needsAttention > 0 && (
                        <Badge 
                          badgeContent={needsAttention} 
                          color="warning"
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: '16px', height: '16px' } }}
                        />
                      )}
                    </Box>
                  ) : undefined
                }
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Box 
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          pt: 1, // Minimal top padding
          width: '100%', // Full width since drawer is now overlaid instead of pushed
          // Removed left margin and left offset since drawer is now on the right side
        }}
      >
        {/* Header with navigation controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {active !== 'landing' && (
              <Button
                startIcon={<ArrowLeftIcon size={16} />}
                onClick={() => setActive('landing')}
                variant="outlined"
                size="small"
              >
                Back to Menu
              </Button>
            )}
            {needsEventEdition && (
              <>
                <EventEditionSelector />
              </>
            )}
          </Box>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <MenuIcon size={20} />
          </IconButton>
        </Box>
        <ResultsStatusBanner />
        <Box sx={{ mt: 1, px: 0.5 }}>
            {active === 'landing' && <AdminLandingPanel onSelect={(k: AdminSectionKey) => setActive(k)} logCounts={{ unresolved, needsAttention }} />}
            {active === 'analytics' && <AnalyticsPanel />}
            {active === 'registrations' && <RegistrationsPanel />}
            {active === 'templates' && <TemplatesPanel />}
            {active === 'bulkemail' && <BulkEmailPanel />}
            {active === 'mmcemail' && <MmcEmailPocPanel />}
            {active === 'codelists' && <CodeListPanel />}
            {active === 'editions' && <EventEditionsPanel />}
            {active === 'actions' && <ActionRequestsPanel />}
            {active === 'tasks' && <AdminTasksPanel />}
            {active === 'bibassignment' && <BibAssignmentPanel />}
            {active === 'resultsimport' && <MOResultsImportPanel />}
            {active === 'kutcexport' && <KUTCResultExportPanel />}
            {active === 'depositrefunds' && <DepositRefundPanel />}
            {active === 'movolunteers' && <MOVolunteerPanel />}
            {active === 'logs' && <LogsPanel />}
          </Box>
      </Box>
    </Box>
  );
};

export default AdminPage;
